<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"
import { useStore } from "vuex"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardSubtitle from "@/components/shared/CardSubtitle.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InfoRow from "@/components/shared/InfoRow.vue"
import InfoTable from "@/components/shared/InfoTable.vue"
import InputBox from "@/components/shared/InputBox.vue"
import InputSelect from "@/components/shared/InputSelect.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import StatusBadge from "@/components/shared/StatusBadge.vue"
import Table from "@/components/shared/Table.vue"
import TableImage from "@/components/shared/TableImage.vue"
import { getLocaleMessages } from "@/locales/index"
import router from "@/plugins/router"
import stockInquiryService, { type ConfirmRequest } from "@/services/stock-inquiry"
import type { StockCheckDetail, StockCheckProduct } from "@/types/stock-check.type"
import { reportSentry } from "@/utils/sentry"
import { formattedDate, unitedNumber } from "@/utils/string"

const store = useStore()
const route = useRoute()
const { t } = useI18n()

const inquiry = ref<StockCheckDetail | null>(null)
const products = ref<StockCheckProduct[]>([])

const id = computed(() => route.params.id)
const pageTitle = computed(() => {
  return `${t("stockInquiry.title")} #${inquiry.value?.code || ""}`
})

onMounted(async () => {
  try {
    const data = await stockInquiryService.get(Number(id.value))
    inquiry.value = data
    products.value = data.stockCheckProducts
  } catch (error) {
    console.error(error)
    reportSentry(error)
    router.replace("/stock_inquiries")
  }
})

const isSubmitting = ref(false)

async function submitForm() {
  isSubmitting.value = true

  const payload = { stockCheckProducts: [] } as ConfirmRequest
  products.value.forEach((product) => {
    payload.stockCheckProducts.push({
      productId: product.product.id,
      stock: product.stock,
      reproductionAvailability: product.reproductionAvailability,
      reproductionQuantity: product.reproductionQuantity,
      reproductionDays: product.reproductionDays,
    })
  })

  try {
    const data = await stockInquiryService.confirm(Number(id.value), payload)

    inquiry.value = data
    store.dispatch("loadPartner", { force: true })
  } catch (err) {
    console.log(err)
    reportSentry(err)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <LayoutAuthed :title="pageTitle">
    <Card v-if="inquiry">
      <CardContent>
        <InfoTable>
          <InfoRow :name="$t('stockInquiry.customerCode')">{{ inquiry.customerCode }}</InfoRow>
          <InfoRow :name="$t('stockInquiry.inquiryDate')">{{ formattedDate(inquiry.createdAt) }}</InfoRow>
          <InfoRow v-if="inquiry.status === 'completed'" :name="$t('stockInquiry.replyDate')">
            {{ formattedDate(inquiry.updatedAt) }}
          </InfoRow>
          <InfoRow :name="$t('stockInquiry.status')">
            <StatusBadge model="stockInquiry" field="status" :value="inquiry.status" />
          </InfoRow>
        </InfoTable>
      </CardContent>
    </Card>

    <Card v-if="inquiry && products.length > 0 && inquiry.status === 'requested'">
      <CardTitle>{{ t("stockInquiry.inputResults") }}</CardTitle>
      <CardSubtitle>{{ t("stockInquiry.guides.insertStock") }}</CardSubtitle>
      <form @submit.prevent="submitForm()">
        <CardContent>
          <Table>
            <template #header>
              <tr>
                <th>{{ t("stockInquiry.item.image") }}</th>
                <th>{{ t("stockInquiry.item.name") }}</th>
                <th>{{ t("stockInquiry.item.colorCode") }}</th>
                <th>{{ t("stockInquiry.item.requestedQuantity") }}</th>
                <th>{{ t("stockInquiry.item.approxStock") }}</th>
                <th>{{ t("stockInquiry.item.reproductionAvailability") }}</th>
                <th>{{ t("stockInquiry.item.reproductionQuantity") }}</th>
                <th>{{ t("stockInquiry.item.reproductionDays") }}</th>
              </tr>
            </template>
            <template #default>
              <tr v-for="(product, idx) in products" :key="idx">
                <td>
                  <TableImage v-if="product.product.image" :image="product.product.image" />
                  <span v-else>-</span>
                </td>
                <td>{{ product.product.quality.name }}</td>
                <td>{{ product.product.colorCode }}</td>
                <td>{{ unitedNumber(product.requestedQuantity, $t("item.units.yards_short")) }}</td>
                <td>
                  <InputBox
                    type="number"
                    v-model="product.stock"
                    field="stock"
                    :min="0"
                    :suffix="$t('shared.units.yards_short')"
                    :required="true"
                  />
                </td>
                <td>
                  <InputSelect
                    v-model="product.reproductionAvailability"
                    field="reproductionAvailability"
                    :options="
                      Object.entries(getLocaleMessages().stockInquiry.item.reproductionAvailabilityList).map(
                        ([v, k]) => ({ label: k, value: v }),
                      )
                    "
                    required
                  />
                </td>
                <td>
                  <InputBox
                    v-if="product.reproductionAvailability === 'available'"
                    type="number"
                    v-model="product.reproductionQuantity"
                    field="reproductionQuantity"
                    :min="1"
                    :suffix="$t('shared.units.yards_short')"
                    :required="true"
                  />
                </td>
                <td>
                  <InputBox
                    v-if="product.reproductionAvailability === 'available'"
                    type="number"
                    v-model="product.reproductionDays"
                    field="reproductionDays"
                    :min="1"
                    :suffix="$t('shared.units.days')"
                    :required="true"
                  />
                </td>
              </tr>
            </template>
          </Table>
        </CardContent>
        <CardActions justifyEnd>
          <Button type="submit" :text="$t('shared.save')" color="danger" class="mt-4" :isSubmitting="isSubmitting" />
        </CardActions>
      </form>
    </Card>

    <Card v-if="inquiry && products.length > 0 && inquiry.status === 'completed'">
      <CardTitle>{{ t("stockInquiry.items") }} ({{ products.length }})</CardTitle>
      <CardContent>
        <Table>
          <template #header>
            <tr>
              <th>{{ t("stockInquiry.item.image") }}</th>
              <th>{{ t("stockInquiry.item.name") }}</th>
              <th>{{ t("stockInquiry.item.colorCode") }}</th>
              <th>{{ t("stockInquiry.item.requestedQuantity") }}</th>
              <th>{{ t("stockInquiry.item.repliedStock") }}</th>
              <th>{{ t("stockInquiry.item.reproductionAvailability") }}</th>
              <th>{{ t("stockInquiry.item.reproductionQuantity") }}</th>
              <th>{{ t("stockInquiry.item.reproductionDays") }}</th>
            </tr>
          </template>
          <template #default>
            <tr v-for="(product, idx) in products" :key="idx">
              <td>
                <TableImage v-if="product.product.image" :image="product.product.image" />
                <span v-else>-</span>
              </td>
              <td>{{ product.product.quality.name }}</td>
              <td>{{ product.product.colorCode }}</td>
              <td>{{ unitedNumber(product.requestedQuantity, $t("item.units.yards_short")) }}</td>
              <td>{{ product.stock }} {{ $t("shared.units.yards_short") }}</td>
              <td>
                {{
                  $t(`stockInquiry.item.reproductionAvailabilityList.${product.reproductionAvailability}`, 0, {
                    default: "-",
                  })
                }}
              </td>
              <td>{{ product.reproductionQuantity }} {{ $t("shared.units.yards_short") }}</td>
              <td>{{ product.reproductionDays }} {{ $t("shared.units.days") }}</td>
            </tr>
          </template>
        </Table>
      </CardContent>
    </Card>
  </LayoutAuthed>
</template>

<style scoped></style>
